const createUuid = () => {
    if (window.crypto?.randomUUID) {
        return window.crypto.randomUUID();
    }
    const values = new Uint8Array(16);
    if (window.crypto?.getRandomValues) {
        window.crypto.getRandomValues(values);
    } else {
        values.forEach((_, index) => {
            values[index] = Math.floor(Math.random() * 256);
        });
    }
    values[6] = (values[6] & 0x0f) | 0x40;
    values[8] = (values[8] & 0x3f) | 0x80;
    const hex = Array.from(values, (value) => value.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

const canonicalize = (value) => {
    if (Array.isArray(value)) {
        return value.map(canonicalize);
    }
    if (value && typeof value === 'object') {
        return Object.keys(value)
            .sort()
            .reduce((result, key) => {
                if (value[key] !== undefined) {
                    result[key] = canonicalize(value[key]);
                }
                return result;
            }, {});
    }
    return value;
};

const fingerprintPayload = (payload) => JSON.stringify(canonicalize(payload));

const readStoredAttempt = (storageKey) => {
    if (!storageKey) {
        return null;
    }
    try {
        const value = JSON.parse(window.sessionStorage.getItem(storageKey) || 'null');
        return value?.key && value?.fingerprint ? value : null;
    } catch (error) {
        return null;
    }
};

const writeStoredAttempt = (storageKey, attempt) => {
    if (!storageKey) {
        return;
    }
    try {
        if (attempt) {
            window.sessionStorage.setItem(storageKey, JSON.stringify(attempt));
        } else {
            window.sessionStorage.removeItem(storageKey);
        }
    } catch (error) {
        // Storage is optional. The in-memory attempt still protects retries on this page.
    }
};

export const shouldRetainPostingOperationAttempt = (error) => {
    const errorCode = String(error?.error_code || '');
    if (['operation_in_progress', 'operation_outcome_unknown'].includes(errorCode)) {
        return true;
    }
    const status = Number(error?.httpStatus ?? error?.status);
    // Access or workspace conflicts cannot rule out an earlier accepted attempt.
    if ([401, 403, 404, 409].includes(status)) {
        return true;
    }
    return !Number.isFinite(status) || status === 0 || status === 408 || status >= 500;
};

export const createPostingOperationAttemptTracker = ({storageKey = ''} = {}) => {
    let attempt = readStoredAttempt(storageKey);

    const setAttempt = (value) => {
        attempt = value;
        writeStoredAttempt(storageKey, value);
    };

    return {
        getKey(payload) {
            const fingerprint = fingerprintPayload(payload);
            if (!attempt || attempt.fingerprint !== fingerprint) {
                setAttempt({key: createUuid(), fingerprint});
            }
            return attempt.key;
        },
        complete() {
            setAttempt(null);
        },
        handleError(error) {
            if (!shouldRetainPostingOperationAttempt(error)) {
                setAttempt(null);
            }
        },
        reset() {
            setAttempt(null);
        },
    };
};

export const getPostingOperationErrorMessage = (error, translate) => {
    if (typeof translate !== 'function') {
        return '';
    }
    const messages = {
        operation_idempotency_conflict: [
            'ads:operationIdempotency.conflict',
            'This operation changed after its retry key was created. Review it and submit again.',
        ],
        operation_in_progress: [
            'ads:operationIdempotency.inProgress',
            'This operation is already being processed. Wait and retry the same request.',
        ],
        operation_outcome_unknown: [
            'ads:operationIdempotency.outcomeUnknown',
            'The operation may already have been accepted. Do not start it again with a new request.',
        ],
        operation_idempotency_key_invalid: [
            'ads:operationIdempotency.invalidKey',
            'The safe retry key is invalid. Reopen the operation and try again.',
        ],
    };
    const message = messages[String(error?.error_code || '')];
    return message ? translate(message[0], {defaultValue: message[1]}) : '';
};
